// NOTE(DS migration, GOTU-3282): layout/text/color primitives + Button migrated to @tempmee/react.
// Remaining commonComponents here (BackButton/CustomPortal) have no DS equivalent yet
// and are kept intentionally (separate shared-component migration category).
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Button, Screen } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents/CenteredSpinner';
import history from '~/browserHistory';
import { BackButton } from '~/commonComponents';
import { jobKeys, useInviteProfessionalsToJobsMutation } from '~/domain/Jobs';
import { useUserSelector } from '~/hooks';
import { fireInviteSent, fireShiftSelected } from '~/posthogs/events';
import {
  FETCH_OPEN_JOBS_FOR_RECENT_PROFESSIONAL_ACTION,
  fetchOpenJobsForRecentProfessional,
} from '../../../../actions';
import { clearSuccess } from '../../../../actions/success';
import { CustomPortal } from '../../../../commonComponents';
import useLoading from '../../../../hooks/useLoading';
import { colors } from '@tempmee/tailwind';
import { JobCard } from './JobCard';

export const InviteProfessional = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const inviteProfessionalsToJobsMutation = useInviteProfessionalsToJobsMutation();
  const professional = history?.location?.state?.professional;
  const [showSuccessProfessionalInvitedModal, setShowSuccessProfessionalInvitedModal] =
    useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const recentOpenJobs = useSelector((state) => state.job.recentOpenJobs);
  const isScrollLoading = recentOpenJobs?.isActiveRequest;
  const selectedOffice = useUserSelector();
  const isLoadingInvite = inviteProfessionalsToJobsMutation.isPending;

  const handleSelect = (id) => {
    if (selectedJobs.includes(id)) {
      setSelectedJobs(selectedJobs.filter((jobId) => jobId !== id));
    } else {
      setSelectedJobs([...selectedJobs, id]);

      const job = recentOpenJobs?.offers?.find((j) => j.id === id);
      fireShiftSelected({
        shiftId: id,
        shiftStatus: job?.job_status === 'open' ? 'Open' : 'Action Needed',
      });
    }
  };

  const handleSendButton = () => {
    fireInviteSent({
      professionalId: professional.id,
      totalInvitesSent: selectedJobs.length,
      source: 'Professional Hub',
    });

    inviteProfessionalsToJobsMutation.mutate(
      {
        jobIds: selectedJobs,
        professionalIds: [professional.id],
        officeId: selectedOffice.id,
      },
      {
        onSuccess: (_data, payload) => {
          payload.jobIds.forEach((jobId) => {
            queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
          });
          setShowSuccessProfessionalInvitedModal(true);
          setSelectedJobs([]);
        },
      },
    );
  };

  const handleScrollList = useCallback(
    (event) => {
      const tableScrollHeight = event?.target?.scrollHeight - event?.target?.scrollTop - 20;
      const clientHeight = event?.target?.clientHeight;
      if (
        tableScrollHeight <= clientHeight &&
        !recentOpenJobs.isActiveRequest &&
        !recentOpenJobs.isFinal
      ) {
        dispatch(
          fetchOpenJobsForRecentProfessional({
            professionalId: professional?.id,
            profession: professional?.profession,
            page: recentOpenJobs?.page + 1,
          }),
        );
      }
    },
    [recentOpenJobs, dispatch, professional],
  );

  useEffect(() => {
    if ((professional?.id && recentOpenJobs?.offers.length === 0) || !professional) {
      history.push('/professionals-hub');
    }

    return () => {
      dispatch(clearSuccess([FETCH_OPEN_JOBS_FOR_RECENT_PROFESSIONAL_ACTION]));
    };
  }, [recentOpenJobs, history, professional, dispatch]);

  return (
    <>
      <Screen variant="standard">
        <Screen.Header start={<BackButton />} title="Invite Professional" />
        <Screen.Body style={{ alignItems: 'center' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: 12,
            }}
          >
            <Avatar size="2xl" src={professional?.image || undefined} alt="Professional" />

            <span
              style={{
                marginTop: 28,
                fontSize: 16,
                fontFamily: 'Nunito',
                color: colors.neutral['600'],
                marginBottom: 40,
              }}
            >
              Select the shifts below that you would like to invite <b>{professional?.name}</b> to.
            </span>
          </div>

          {isLoadingInvite ? (
            <CenteredSpinner />
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 12,
                  paddingTop: 4,
                  paddingBottom: 4,
                  paddingRight: 8,
                  width: 860,
                  minHeight: 300,
                  maxHeight: 600,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarWidth: 'thin',
                }}
                onScroll={handleScrollList}
              >
                {recentOpenJobs?.offers?.map((job) => (
                  <JobCard
                    key={job.id}
                    job={{
                      id: job.id,
                      status: job.job_status,
                      date: job.local_date,
                      professionName: job.professionName,
                      subProfession: job.subProfession,
                      startTime: job.local_start_time,
                      endTime: job.local_end_time,
                    }}
                    isChecked={selectedJobs.includes(job.id)}
                    onSelect={handleSelect}
                  />
                ))}
                {isScrollLoading && (
                  <div style={{ marginTop: 20 }}>
                    <CenteredSpinner />
                  </div>
                )}
              </div>

              {/* dims kept inline to preserve the legacy button box (296x48); DS size variants
                  don't match these exact dimensions */}
              <Button
                disabled={selectedJobs.length === 0}
                onClick={handleSendButton}
                style={{ width: 296, height: 48, marginTop: 60 }}
              >
                Send
              </Button>
            </div>
          )}
        </Screen.Body>
      </Screen>

      {showSuccessProfessionalInvitedModal && (
        <CustomPortal
          isSuccess
          redirect={false}
          title="Request(s) Successfully Sent!"
          text={
            <>
              <b>{professional.name}</b> has been sent a direct invite to the selected shift(s). If
              accepted at the listed rate, {professional.name.split(' ')[0]} will be confirmed!
              Visit Professionals Hub &gt; Invites page to review the status of your sent
              invitations.
            </>
          }
          buttonText="Close"
          submitPressed={() => {
            setShowSuccessProfessionalInvitedModal(false);
          }}
        />
      )}
    </>
  );
};
